// =============================================================================
// ComplianceMatrixScreen — /hr/compliance/matrix
// =============================================================================
// Three tabs:
//
//   READINESS   everyone, worst first. The question the feature exists for:
//               who can go on site tomorrow.
//   PEOPLE      the matrix, edited the way James actually works - open a
//               person, tick what they are. Primary role and its class, task
//               sign-offs, plant, site inductions. One screen per person.
//   ROLE RULES  what each role requires. Rarely touched, so it is behind the
//               people tab rather than in front of it.
//
// WHY PEOPLE COMES BEFORE ROLES
// -----------------------------
// The first version made you pick a role and then tick people into it. James's
// verdict: "it's too hard to add people to a role, I would rather see that
// employee and manage / tick things like their main role." He is right, and it
// is not just preference - the work arrives per person. Somebody hands in a
// ticket, somebody gets promoted, somebody starts. Nobody ever wakes up wanting
// to edit "Confined Space Entry" across the whole company.
//
// CLASS IS AN ATTRIBUTE OF THE PERSON
// -----------------------------------
// There is one role called Rigger, not three. The class - Basic, Intermediate,
// Advanced - sits on the person, and the requirement generated from it is "a
// Dogging & Rigging ticket at that class or higher". Same for Driver. See
// migration 318.
//
// Everything on Readiness is DERIVED. No compliance figure is stored anywhere:
// change a rule here and the numbers move on the next refetch.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, ShieldCheck, Users, Settings2, Plus, Trash2, ChevronRight,
  ChevronDown, AlertTriangle, FileText, Check, Sparkles, IdCard, EyeOff,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Button, Input, Select, Spinner, EmptyState, Pill } from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import PersonReadinessPanel from "./PersonReadinessPanel";
import {
  ReadinessBar, ReadinessLegend, StatusPill, readiness, bestStatus,
  ROLE_KIND_META, type StatusCounts, type ReqStatus,
} from "./complianceShared";
import { downloadComplianceReportPdf } from "./complianceReportPdf";

type Tab = "readiness" | "people" | "rules";

interface Person {
  employee_id: string; first_name: string; last_name: string;
  primary_role_name: string | null;
  // Is the site-readiness question one that applies to this person at all?
  // Off for office staff, test logins and anyone who never attends site.
  in_site_compliance: boolean;
}
interface Summary {
  employee_id: string; required_groups: number; pct_compliant: number | null;
  current_count: number; expiring_count: number; pending_count: number;
  expired_count: number; missing_count: number; next_expiry: string | null;
}
interface Role {
  role_id: string; code: string; display_name: string; kind: string;
  is_baseline: boolean; sort_order: number; is_active: boolean;
  grade_family: string | null; auto_grant: boolean; notes: string | null;
}
interface Requirement {
  requirement_id: string; role_id: string; licence_type_id: string;
  alt_group: string | null; is_mandatory: boolean; notes: string | null;
}
interface LicenceType {
  licence_type_id: string; code: string; display_name: string; category: string;
}
interface Grade {
  grade_family: string; grade_code: string; grade_label: string;
  grade_rank: number; licence_type_id: string;
}
interface Assignment {
  employee_role_id: string; employee_id: string; role_id: string;
  grade: string | null; notes: string | null;
}
interface HeldLicence {
  employee_id: string; licence_type_id: string;
  expiry_date: string | null; verified_at: string | null;
}

// -----------------------------------------------------------------------------
// The status of one ticket a person holds, by the same rules the database uses.
// Duplicated here on purpose and nowhere else: it drives the little hint marks
// on the tick boxes, which have to be right BEFORE you tick, and the database
// only reports on roles somebody already holds.
// -----------------------------------------------------------------------------
function licenceStatus(l: HeldLicence | undefined): ReqStatus {
  if (!l) return "missing";
  if (l.expiry_date && l.expiry_date < new Date().toISOString().slice(0, 10)) return "expired";
  if (!l.verified_at) return "pending";
  if (l.expiry_date) {
    const soon = new Date();
    soon.setDate(soon.getDate() + 60);
    if (l.expiry_date <= soon.toISOString().slice(0, 10)) return "expiring";
  }
  return "current";
}

export default function ComplianceMatrixScreen() {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("readiness");
  const [openPerson, setOpenPerson] = useState<string | null>(null);
  const [editPerson, setEditPerson] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);

  // ---------------------------------------------------------------------------
  const { data: people = [] } = useQuery({
    queryKey: ["compliance_people"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name, in_site_compliance")
        // Somebody who has left the company was being measured. Straight bug.
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as unknown as Person[];
    },
  });

  const { data: summaries = [], isLoading: loadingSummary } = useQuery({
    queryKey: ["compliance_summaries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employee_compliance_summary")
        .select("*");
      if (error) throw error;
      return (data ?? []) as unknown as Summary[];
    },
  });

  const { data: roles = [] } = useQuery({
    queryKey: ["compliance_roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("compliance_roles")
        .select("role_id, code, display_name, kind, is_baseline, sort_order, is_active, grade_family, auto_grant, notes")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as Role[];
    },
  });

  const { data: requirements = [] } = useQuery({
    queryKey: ["compliance_requirements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("role_requirements")
        .select("requirement_id, role_id, licence_type_id, alt_group, is_mandatory, notes");
      if (error) throw error;
      return (data ?? []) as Requirement[];
    },
  });

  const { data: licenceTypes = [] } = useQuery({
    queryKey: ["compliance_licence_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("licence_types")
        .select("licence_type_id, code, display_name, category")
        .eq("is_active", true)
        .order("category")
        .order("display_name");
      if (error) throw error;
      return (data ?? []) as LicenceType[];
    },
  });

  const { data: grades = [] } = useQuery({
    queryKey: ["compliance_grades"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("licence_grades")
        .select("grade_family, grade_code, grade_label, grade_rank, licence_type_id")
        .order("grade_rank");
      if (error) throw error;
      return (data ?? []) as unknown as Grade[];
    },
  });

  const { data: assignments = [] } = useQuery({
    queryKey: ["compliance_assignments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_roles")
        .select("employee_role_id, employee_id, role_id, grade, notes");
      if (error) throw error;
      return (data ?? []) as unknown as Assignment[];
    },
  });

  // Every live ticket on file, for everyone. Fifteen people times a dozen
  // tickets - small enough to hold, and it is what makes the tick boxes able to
  // say "already on file" before you tick them.
  const { data: held = [] } = useQuery({
    queryKey: ["compliance_held_licences"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select("employee_id, licence_type_id, expiry_date, verified_at")
        .eq("is_active", true)
        .is("superseded_at", null);
      if (error) throw error;
      return (data ?? []) as unknown as HeldLicence[];
    },
  });

  // ---------------------------------------------------------------------------
  const summaryBy = useMemo(() => new Map(summaries.map(s => [s.employee_id, s])), [summaries]);
  const licenceBy = useMemo(() => new Map(licenceTypes.map(l => [l.licence_type_id, l])), [licenceTypes]);
  const rolesBy   = useMemo(() => new Map(roles.map(r => [r.role_id, r])), [roles]);
  const activeRoles = useMemo(() => roles.filter(r => r.is_active), [roles]);

  // Best ticket of each type per person. "Best" is current beats expired, then
  // latest expiry - the same order the database view uses.
  const heldBy = useMemo(() => {
    const m = new Map<string, HeldLicence>();
    const today = new Date().toISOString().slice(0, 10);
    for (const l of held) {
      const k = `${l.employee_id}::${l.licence_type_id}`;
      const prev = m.get(k);
      if (!prev) { m.set(k, l); continue; }
      const live = (x: HeldLicence) => !x.expiry_date || x.expiry_date >= today;
      if (live(l) && !live(prev)) { m.set(k, l); continue; }
      if (live(l) === live(prev) && (l.expiry_date ?? "9999") > (prev.expiry_date ?? "9999")) m.set(k, l);
    }
    return m;
  }, [held]);

  const rows = useMemo(() => {
    const list = people.map(p => {
      const s = summaryBy.get(p.employee_id);
      const counts: StatusCounts = {
        current:  s?.current_count  ?? 0,
        expiring: s?.expiring_count ?? 0,
        pending:  s?.pending_count  ?? 0,
        expired:  s?.expired_count  ?? 0,
        missing:  s?.missing_count  ?? 0,
      };
      const required = s?.required_groups ?? 0;
      const mine = assignments.filter(a => a.employee_id === p.employee_id);
      return {
        p, s, counts, required,
        roleCount: mine.length,
        pct: s?.pct_compliant ?? null,
        verdict: readiness(s?.pct_compliant ?? null, counts, required),
      };
    });
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? list.filter(r => `${r.p.first_name} ${r.p.last_name}`.toLowerCase().includes(needle))
      : list;
    // Worst first. This screen exists to surface problems, so problems go on top.
    return filtered.sort((a, b) =>
      (a.pct ?? -1) - (b.pct ?? -1)
      || `${a.p.first_name} ${a.p.last_name}`.localeCompare(`${b.p.first_name} ${b.p.last_name}`));
  }, [people, summaryBy, assignments, q]);

  // Everything on this screen counts people in scope. Somebody excluded is not
  // "0% compliant" - the question does not apply to them, and averaging them in
  // produces a headline figure nobody believes.
  const tracked   = useMemo(() => rows.filter(r => r.p.in_site_compliance), [rows]);
  const untracked = useMemo(() => rows.filter(r => !r.p.in_site_compliance), [rows]);

  const kpis = useMemo(() => {
    const withRoles = tracked.filter(r => r.required > 0);
    return {
      ready:    withRoles.filter(r => r.verdict.band === "ready").length,
      renewals: withRoles.filter(r => r.verdict.band === "renewals").length,
      gaps:     withRoles.filter(r => r.verdict.band === "gaps").length,
      notReady: withRoles.filter(r => r.verdict.band === "not_ready").length,
      noRoles:  tracked.filter(r => r.roleCount === 0).length,
      expiring: tracked.reduce((n, r) => n + (r.counts.expiring ?? 0), 0),
    };
  }, [tracked]);

  const rolesByKind = useMemo(() => {
    const m = new Map<string, Role[]>();
    for (const r of activeRoles) {
      const list = m.get(r.kind) ?? [];
      list.push(r);
      m.set(r.kind, list);
    }
    return Array.from(m.entries())
      .sort((a, b) => (ROLE_KIND_META[a[0]]?.order ?? 9) - (ROLE_KIND_META[b[0]]?.order ?? 9));
  }, [activeRoles]);

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["compliance_people"] });
    qc.invalidateQueries({ queryKey: ["compliance_requirements"] });
    qc.invalidateQueries({ queryKey: ["compliance_assignments"] });
    qc.invalidateQueries({ queryKey: ["compliance_summaries"] });
    qc.invalidateQueries({ queryKey: ["compliance_held_licences"] });
    qc.invalidateQueries({ queryKey: ["readiness_rows"] });
  };

  async function makeReport() {
    setBuilding(true); setErr(null);
    try {
      await downloadComplianceReportPdf();
    } catch (e: any) {
      setErr(`Couldn't build the report: ${e?.message ?? e}`);
    } finally {
      setBuilding(false);
    }
  }

  if (!user) return null;
  if (!canManage) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState icon={AlertTriangle} title="Managers only"
            description="The compliance matrix is available to managers and directors. Your own readiness is on your portal." />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1180px] mx-auto pb-20">
      <Link to="/hr" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14} /> HR portal
      </Link>

      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck size={20} className="text-accent" /> Compliance matrix
          </h1>
          <p className="text-text-muted text-[13px] mt-1 max-w-2xl">
            Who can work on site, what they're missing, and the rules that decide it.
            Nothing here is stored — it's worked out from the tickets already on file.
          </p>
        </div>
        <Button variant="secondary" size="sm" icon={FileText}
                onClick={makeReport} disabled={building}>
          {building ? "Building…" : "Compliance report (PDF)"}
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {([
          ["readiness", "Readiness", Users],
          ["people",    "People",    IdCard],
          ["rules",     "Role rules", Settings2],
        ] as const).map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium border transition-colors ${
              tab === k ? "bg-accent text-white border-accent" : "border-line text-text-muted hover:text-text"
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {err && (
        <Card className="mb-4">
          <div className="p-3 text-[12px] text-danger">{err}</div>
        </Card>
      )}

      {/* ===================================================================== */}
      {/* READINESS                                                             */}
      {/* ===================================================================== */}
      {tab === "readiness" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KPI label="Site ready" value={String(kpis.ready)} sub="all requirements met" />
            <KPI label="Renewals due" value={String(kpis.renewals)} sub="ready, but expiring" accent={kpis.renewals > 0} />
            <KPI label="Not site ready" value={String(kpis.notReady + kpis.gaps)} sub="gaps to close" accent={kpis.notReady > 0} />
            <KPI label="No roles set" value={String(kpis.noRoles)} sub="measured on baseline only" accent={kpis.noRoles > 0} />
          </div>

          {kpis.noRoles > 0 && (
            <Card className="mb-4">
              <div className="p-4 flex items-start gap-2.5">
                <AlertTriangle size={16} className="text-warn mt-0.5 flex-shrink-0" />
                <div className="text-[13px] text-text">
                  {kpis.noRoles} {kpis.noRoles === 1 ? "person has" : "people have"} no roles set,
                  so they're only measured against the baseline every field worker holds.
                  <div className="text-[12px] text-text-dim mt-0.5">
                    That's why some read 100% — it means "meets the baseline", not "ready for anything".
                    Set them up under <button type="button" className="text-accent underline" onClick={() => setTab("people")}>People</button>.
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card className="mb-4">
            <div className="p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px] max-w-sm">
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a person…" />
              </div>
              <ReadinessLegend />
            </div>
          </Card>

          {loadingSummary ? (
            <div className="py-10 flex justify-center"><Spinner size={18} /></div>
          ) : (
            <div className="space-y-2">
              {tracked.map(({ p, counts, pct, verdict, required, roleCount, s }) => {
                const open = openPerson === p.employee_id;
                const Icon = verdict.icon;
                return (
                  <Card key={p.employee_id}>
                    <button type="button"
                      onClick={() => setOpenPerson(open ? null : p.employee_id)}
                      className="w-full text-left p-4 flex items-center gap-4">
                      {open
                        ? <ChevronDown size={16} className="text-text-muted flex-shrink-0" />
                        : <ChevronRight size={16} className="text-text-muted flex-shrink-0" />}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[14px] font-medium text-text">
                            {p.first_name} {p.last_name}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${verdict.chip} ${verdict.text}`}>
                            <Icon size={10} /> {verdict.label}
                          </span>
                          {roleCount === 0 && <Pill label="not set up" tone="warning" small />}
                        </div>
                        <div className="mt-2 max-w-md">
                          <ReadinessBar counts={counts} height={8} />
                        </div>
                        <div className="text-[11px] text-text-muted mt-1.5">
                          {required} requirement{required === 1 ? "" : "s"}
                          {(counts.expired ?? 0) > 0 && <> · <span className="text-danger">{counts.expired} expired</span></>}
                          {(counts.missing ?? 0) > 0 && <> · {counts.missing} missing</>}
                          {s?.next_expiry && <> · next expiry {fmtDate(s.next_expiry)}</>}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="text-[20px] font-semibold text-text tabular-nums leading-none">
                          {pct == null ? "—" : `${pct}%`}
                        </div>
                      </div>
                    </button>

                    {open && (
                      <div className="border-t border-line p-4 bg-ink-raised">
                        <div className="flex justify-end mb-3">
                          <Button size="sm" variant="ghost" icon={Settings2}
                            onClick={() => { setEditPerson(p.employee_id); setTab("people"); }}>
                            Edit what {p.first_name} does
                          </Button>
                        </div>
                        <PersonReadinessPanel employeeId={p.employee_id} compact allowEdit />
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* ---- people the question does not apply to --------------------
              Listed rather than hidden. A person silently absent from a
              compliance report is how somebody gets missed; a person visibly
              excluded is a decision anyone can check.                      */}
          {untracked.length > 0 && (
            <Card className="mt-4">
              <div className="p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
                  <h3 className="text-[13px] font-semibold text-text">
                    Not tracked for site compliance
                  </h3>
                  <span className="text-[11px] text-text-muted">{untracked.length}</span>
                </div>
                <p className="text-[11px] text-text-muted mb-2.5">
                  Office staff, test logins, anyone who doesn't attend site. They're left out
                  of the figures above and out of the report — their licence records are
                  untouched.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {untracked.map(({ p }) => (
                    <button key={p.employee_id} type="button"
                      onClick={() => { setEditPerson(p.employee_id); setTab("people"); }}
                      className="px-2 py-1 rounded border border-line text-[11.5px] text-text-muted hover:border-accent hover:text-accent transition-colors">
                      {p.first_name} {p.last_name}
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ===================================================================== */}
      {/* PEOPLE — the matrix, per person                                       */}
      {/* ===================================================================== */}
      {tab === "people" && (
        <div className="grid md:grid-cols-[260px_1fr] gap-4 items-start">
          <Card>
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-semibold px-1 mb-2">
                Who
              </div>
              <div className="mb-2">
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a person…" />
              </div>
              <div className="space-y-0.5">
                {tracked.map(({ p, counts, pct, roleCount }) => (
                  <button key={p.employee_id} type="button"
                    onClick={() => setEditPerson(p.employee_id)}
                    className={`w-full text-left px-2 py-2 rounded transition-colors ${
                      editPerson === p.employee_id ? "bg-accent/12" : "hover:bg-ink-hover"
                    }`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[12.5px] truncate ${editPerson === p.employee_id ? "text-accent" : "text-text"}`}>
                        {p.first_name} {p.last_name}
                      </span>
                      <span className="text-[11px] text-text-muted tabular-nums flex-shrink-0">
                        {pct == null ? "—" : `${pct}%`}
                      </span>
                    </div>
                    <div className="mt-1.5"><ReadinessBar counts={counts} height={5} /></div>
                    <div className="text-[10px] text-text-dim mt-1">
                      {roleCount === 0 ? "nothing set yet" : `${roleCount} role${roleCount === 1 ? "" : "s"}`}
                    </div>
                  </button>
                ))}

                {untracked.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase tracking-wider2 text-text-dim font-semibold px-1 pt-3 pb-1">
                      Not tracked
                    </div>
                    {untracked.map(({ p }) => (
                      <button key={p.employee_id} type="button"
                        onClick={() => setEditPerson(p.employee_id)}
                        className={`w-full text-left px-2 py-1.5 rounded transition-colors ${
                          editPerson === p.employee_id ? "bg-accent/12" : "hover:bg-ink-hover"
                        }`}>
                        <span className={`text-[12.5px] truncate ${
                          editPerson === p.employee_id ? "text-accent" : "text-text-muted"}`}>
                          {p.first_name} {p.last_name}
                        </span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </Card>

          {!editPerson ? (
            <Card>
              <EmptyState icon={IdCard} title="Pick a person"
                description="Choose someone on the left, then tick what they are and what they're signed off for. Their readiness updates as you go." />
            </Card>
          ) : (
            <EmployeeSetupPanel
              key={editPerson}
              person={people.find(p => p.employee_id === editPerson)!}
              summary={summaryBy.get(editPerson)}
              roles={activeRoles}
              requirements={requirements}
              grades={grades}
              assignments={assignments.filter(a => a.employee_id === editPerson)}
              statusOf={(ltId: string) => licenceStatus(heldBy.get(`${editPerson}::${ltId}`))}
              onChanged={refreshAll}
              onError={setErr}
              onOpenRule={(roleId) => { setSelectedRole(roleId); setTab("rules"); }}
            />
          )}
        </div>
      )}

      {/* ===================================================================== */}
      {/* ROLE RULES                                                            */}
      {/* ===================================================================== */}
      {tab === "rules" && (
        <div className="grid md:grid-cols-[280px_1fr] gap-4 items-start">
          <Card>
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-semibold px-1 mb-2">
                Roles
              </div>
              {rolesByKind.map(([kind, list]) => (
                <div key={kind} className="mb-3">
                  <div className="text-[11px] text-text-muted px-1 mb-1" title={ROLE_KIND_META[kind]?.blurb}>
                    {ROLE_KIND_META[kind]?.label ?? kind}
                  </div>
                  <div className="space-y-0.5">
                    {list.map(r => {
                      const n = requirements.filter(q2 => q2.role_id === r.role_id).length
                              + (r.grade_family ? 1 : 0);
                      const heldCount = assignments.filter(a => a.role_id === r.role_id).length;
                      return (
                        <button key={r.role_id} type="button"
                          onClick={() => setSelectedRole(r.role_id)}
                          className={`w-full text-left px-2 py-1.5 rounded text-[12px] transition-colors ${
                            selectedRole === r.role_id ? "bg-accent/12 text-accent" : "text-text hover:bg-ink-hover"
                          }`}>
                          <div className="flex items-center gap-1.5">
                            <span className="truncate">{r.display_name}</span>
                            {r.is_baseline && <Pill label="everyone" tone="ok" small />}
                          </div>
                          <div className="text-[10px] text-text-dim mt-0.5">
                            {n} requirement{n === 1 ? "" : "s"} · {heldCount} {heldCount === 1 ? "person" : "people"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {!selectedRole ? (
            <Card>
              <EmptyState icon={Settings2} title="Pick a role"
                description="Choose a role to set what it requires. Who holds it is set on the People tab, one person at a time." />
            </Card>
          ) : (
            <RoleEditor
              role={rolesBy.get(selectedRole)!}
              requirements={requirements.filter(r => r.role_id === selectedRole)}
              grades={grades.filter(g => g.grade_family === rolesBy.get(selectedRole)?.grade_family)}
              licenceTypes={licenceTypes}
              licenceBy={licenceBy}
              holders={assignments.filter(a => a.role_id === selectedRole)}
              people={people}
              onChanged={refreshAll}
              onError={setErr}
              onOpenPerson={(id) => { setEditPerson(id); setTab("people"); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EmployeeSetupPanel — one person's whole setup on one screen
// =============================================================================
// This is the screen James asked for: open a person, tick what they are.
//
// Each tick box carries a small mark saying whether the tickets behind it are
// already on file, so the decision is informed rather than blind - "we know
// what's loaded and how compliant they are" was the whole point.
// =============================================================================
function EmployeeSetupPanel({
  person, summary, roles, requirements, grades, assignments,
  statusOf, onChanged, onError, onOpenRule,
}: {
  person: Person;
  summary: Summary | undefined;
  roles: Role[];
  requirements: Requirement[];
  grades: Grade[];
  assignments: Assignment[];
  statusOf: (licenceTypeId: string) => ReqStatus;
  onChanged: () => void;
  onError: (s: string | null) => void;
  onOpenRule: (roleId: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  const heldRole = useMemo(
    () => new Map(assignments.map(a => [a.role_id, a])), [assignments]);

  const counts: StatusCounts = {
    current:  summary?.current_count  ?? 0,
    expiring: summary?.expiring_count ?? 0,
    pending:  summary?.pending_count  ?? 0,
    expired:  summary?.expired_count  ?? 0,
    missing:  summary?.missing_count  ?? 0,
  };
  const verdict = readiness(summary?.pct_compliant ?? null, counts, summary?.required_groups ?? 0);
  const Verdict = verdict.icon;

  // What a role needs, resolved to a single mark. Alternatives (including the
  // class ladder) are OR'd, separate requirements are AND'd - worst wins.
  function roleStatus(role: Role, grade: string | null): ReqStatus {
    const perGroup: ReqStatus[] = [];

    const byGroup = new Map<string, ReqStatus[]>();
    for (const rq of requirements.filter(r => r.role_id === role.role_id)) {
      if (!rq.is_mandatory) continue;
      const k = rq.alt_group ?? `solo:${rq.requirement_id}`;
      byGroup.set(k, [...(byGroup.get(k) ?? []), statusOf(rq.licence_type_id)]);
    }
    for (const list of byGroup.values()) perGroup.push(bestStatus(list));

    if (role.grade_family) {
      const ladder = grades.filter(g => g.grade_family === role.grade_family);
      const minRank = ladder.find(g => g.grade_code === grade)?.grade_rank ?? 1;
      const opts = ladder.filter(g => g.grade_rank >= minRank).map(g => statusOf(g.licence_type_id));
      perGroup.push(opts.length ? bestStatus(opts) : "missing");
    }

    if (!perGroup.length) return "current";  // nothing required, nothing to fail
    // Worst wins across separate requirements.
    for (const s of ["missing", "expired", "pending", "expiring", "current"] as ReqStatus[]) {
      if (perGroup.includes(s)) return s;
    }
    return "current";
  }

  // Whether the site-readiness question applies to this person at all. Not a
  // permission and not a deactivation - their licence records, their portal and
  // their access are all untouched. It only decides whether they are counted.
  async function setTracked(on: boolean) {
    setBusy(true); onError(null);
    const { error } = await supabase.from("employees")
      .update({ in_site_compliance: on })
      .eq("employee_id", person.employee_id);
    setBusy(false);
    if (error) { onError(`Couldn't change that: ${error.message}`); return; }
    onChanged();
  }

  async function setRole(role: Role, on: boolean, grade?: string | null) {
    setBusy(true); onError(null);
    try {
      if (!on) {
        const { error } = await supabase.from("employee_roles").delete()
          .eq("employee_id", person.employee_id).eq("role_id", role.role_id);
        if (error) throw error;
      } else if (heldRole.has(role.role_id)) {
        const { error } = await supabase.from("employee_roles")
          .update({ grade: grade ?? null })
          .eq("employee_id", person.employee_id).eq("role_id", role.role_id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("employee_roles")
          .insert({ employee_id: person.employee_id, role_id: role.role_id, grade: grade ?? null });
        if (error) throw error;
      }
      onChanged();
    } catch (e: any) {
      onError(`Couldn't save that: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  }

  // A person has one job. Picking a new primary role clears the old one, so the
  // list can never end up saying somebody is both a Rigger and a Labourer.
  async function setPrimary(roleId: string) {
    setBusy(true); onError(null);
    try {
      const others = roles.filter(r => r.kind === "primary" && !r.is_baseline && r.role_id !== roleId)
                          .map(r => r.role_id);
      if (others.length) {
        const { error } = await supabase.from("employee_roles").delete()
          .eq("employee_id", person.employee_id).in("role_id", others);
        if (error) throw error;
      }
      if (roleId && !heldRole.has(roleId)) {
        const { error } = await supabase.from("employee_roles")
          .insert({ employee_id: person.employee_id, role_id: roleId });
        if (error) throw error;
      }
      onChanged();
    } catch (e: any) {
      onError(`Couldn't set the main role: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  }

  const primaries = roles.filter(r => r.kind === "primary" && !r.is_baseline);
  const currentPrimary = primaries.find(r => heldRole.has(r.role_id)) ?? null;

  const sections = (["task", "plant", "access"] as const)
    .map(kind => [kind, roles.filter(r => r.kind === kind)] as const)
    .filter(([, list]) => list.length > 0);

  return (
    <div className="space-y-4">
      {/* ---- who, and where they stand ------------------------------------- */}
      <Card>
        <div className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-[17px] font-semibold text-text">
                {person.first_name} {person.last_name}
              </h2>
              <div className="text-[12px] text-text-muted mt-0.5">
                {currentPrimary
                  ? currentPrimary.display_name
                    + (currentPrimary.grade_family
                        ? ` — ${grades.find(g => g.grade_family === currentPrimary.grade_family
                                                && g.grade_code === heldRole.get(currentPrimary.role_id)?.grade)?.grade_label
                              ?? "class not set"}`
                        : "")
                  : person.primary_role_name ?? "No main role set"}
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-baseline gap-2 justify-end">
                <span className="text-[26px] font-semibold text-text tabular-nums leading-none">
                  {summary?.pct_compliant == null ? "—" : `${summary.pct_compliant}%`}
                </span>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${verdict.chip} ${verdict.text} text-[11px] font-medium`}>
                  <Verdict size={11} /> {verdict.label}
                </span>
              </div>
              <div className="text-[11px] text-text-muted mt-1">
                {summary?.required_groups ?? 0} requirement{(summary?.required_groups ?? 0) === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          {person.in_site_compliance ? (
            <>
              <div className="mt-3"><ReadinessBar counts={counts} height={8} /></div>
              <div className="mt-2"><ReadinessLegend counts={counts} /></div>
            </>
          ) : (
            <div className="mt-3 text-[12px] text-text-muted flex items-start gap-1.5">
              <EyeOff size={14} className="mt-0.5 flex-shrink-0" />
              Not tracked for site compliance, so they're left out of the figures and the
              report. Their licence records and their portal are unaffected.
            </div>
          )}

          <label className="mt-4 flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={person.in_site_compliance} disabled={busy}
                   onChange={e => setTracked(e.target.checked)} />
            <span className="text-[12px] text-text">
              Track this person for site compliance
              <span className="block text-[11px] text-text-muted">
                Untick for office staff, test logins, or anyone who never attends site.
              </span>
            </span>
          </label>
        </div>
      </Card>

      {/* ---- main role + class --------------------------------------------- */}
      <Card>
        <div className="p-5">
          <h3 className="text-[13px] font-semibold text-text mb-1">Main role</h3>
          <p className="text-[11px] text-text-muted mb-3">
            What this person is on a job. One each. Where the role has classes,
            set the class too — the requirement becomes that class <em>or higher</em>.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="min-w-[220px] flex-1 max-w-sm">
              <Select value={currentPrimary?.role_id ?? ""} disabled={busy}
                      onChange={e => setPrimary(e.target.value)}>
                <option value="">No main role</option>
                {primaries.map(r => (
                  <option key={r.role_id} value={r.role_id}>{r.display_name}</option>
                ))}
              </Select>
            </div>

            {currentPrimary?.grade_family && (
              <div className="min-w-[180px] max-w-[240px]">
                <Select
                  value={heldRole.get(currentPrimary.role_id)?.grade ?? ""}
                  disabled={busy}
                  onChange={e => setRole(currentPrimary, true, e.target.value || null)}>
                  <option value="">Class not set</option>
                  {grades.filter(g => g.grade_family === currentPrimary.grade_family)
                         .map(g => (
                    <option key={g.grade_code} value={g.grade_code}>{g.grade_label}</option>
                  ))}
                </Select>
              </div>
            )}

            {currentPrimary && (
              <StatusPill status={roleStatus(currentPrimary, heldRole.get(currentPrimary.role_id)?.grade ?? null)} small />
            )}
          </div>

          {currentPrimary?.grade_family && !heldRole.get(currentPrimary.role_id)?.grade && (
            <div className="mt-2.5 text-[11.5px] text-warn flex items-start gap-1.5">
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
              No class set, so this is measured against the lowest one. Set it and the
              requirement tightens to match.
            </div>
          )}

          {currentPrimary && (
            <button type="button" onClick={() => onOpenRule(currentPrimary.role_id)}
                    className="mt-3 text-[11px] text-accent hover:underline">
              What does {currentPrimary.display_name} require? →
            </button>
          )}
        </div>
      </Card>

      {/* ---- everything else, as tick boxes -------------------------------- */}
      {sections.map(([kind, list]) => (
        <Card key={kind}>
          <div className="p-5">
            <div className="flex items-baseline gap-2 mb-1">
              <h3 className="text-[13px] font-semibold text-text">
                {ROLE_KIND_META[kind]?.label ?? kind}
              </h3>
              <span className="text-[11px] text-text-muted">{ROLE_KIND_META[kind]?.blurb}</span>
            </div>
            <p className="text-[11px] text-text-muted mb-3">
              {kind === "task"   && "Extra sign-offs on top of the main role. Ferdy is a Technician who also does tower rescue — this is where that goes."}
              {kind === "plant"  && "Machinery and vehicles. Ticking one adds its tickets to what this person needs."}
              {kind === "access" && "Site inductions. These tick themselves when the induction is loaded onto the person's record — untick one only if they genuinely don't go there."}
            </p>

            <div className="grid sm:grid-cols-2 gap-1.5">
              {list.map(r => {
                const a = heldRole.get(r.role_id);
                const on = !!a;
                const st = roleStatus(r, a?.grade ?? null);
                const auto = !!a?.notes?.includes("automatically");
                return (
                  <div key={r.role_id}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md border transition-colors ${
                      on ? "border-line bg-ink-raised" : "border-line/60"
                    }`}>
                    <label className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer">
                      <input type="checkbox" checked={on} disabled={busy}
                             onChange={e => setRole(r, e.target.checked)} />
                      <span className="min-w-0">
                        <span className={`text-[12.5px] block truncate ${on ? "text-text" : "text-text-muted"}`}>
                          {r.display_name}
                        </span>
                        {auto && (
                          <span className="text-[10px] text-text-dim inline-flex items-center gap-1 mt-0.5">
                            <Sparkles size={9} /> added from a ticket on file
                          </span>
                        )}
                      </span>
                    </label>

                    {on && r.grade_family && (
                      <select
                        value={a?.grade ?? ""}
                        disabled={busy}
                        onChange={e => setRole(r, true, e.target.value || null)}
                        className="bg-ink border border-line rounded px-1.5 py-1 text-[11px] text-text max-w-[130px]">
                        <option value="">Class…</option>
                        {grades.filter(g => g.grade_family === r.grade_family).map(g => (
                          <option key={g.grade_code} value={g.grade_code}>{g.grade_label}</option>
                        ))}
                      </select>
                    )}

                    {/* The hint that makes ticking informed rather than blind. */}
                    {on
                      ? <StatusPill status={st} small />
                      : st === "current"
                        ? <span className="text-[10px] text-ok inline-flex items-center gap-1 whitespace-nowrap"
                                title="The tickets for this are already on file — ticking it costs nothing.">
                            <Check size={10} /> on file
                          </span>
                        : <span className="w-px" />}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      ))}

      {/* ---- what the ticking actually adds up to --------------------------
          The tick boxes above say what somebody IS. This says what that MEANS:
          every ticket those roles need, once each, with its number, its expiry
          and a way straight to the record. James asked for the setup and the
          consequence on one screen rather than two, and he was right - ticking
          a box and then navigating somewhere else to find out what it did is
          how a matrix stops being used.                                     */}
      <div className="pt-2">
        <div className="flex items-baseline gap-2 mb-2">
          <h3 className="text-[13px] font-semibold text-text">What that adds up to</h3>
          <span className="text-[11px] text-text-muted">
            every ticket these roles need, and where it stands right now
          </span>
        </div>
        <PersonReadinessPanel employeeId={person.employee_id} allowEdit />
      </div>

      <div className="text-[11px] text-text-dim">
        Roles are a description of the work, not a permission. Nothing here changes what
        anyone can open in the app — it changes what they're measured against.
      </div>
    </div>
  );
}

// =============================================================================
// RoleEditor — what a role requires
// =============================================================================
// Who holds a role now lives on the People tab. This is only the rule.
// =============================================================================
function RoleEditor({
  role, requirements, grades, licenceTypes, licenceBy, holders, people,
  onChanged, onError, onOpenPerson,
}: {
  role: Role;
  requirements: Requirement[];
  grades: Grade[];
  licenceTypes: LicenceType[];
  licenceBy: Map<string, LicenceType>;
  holders: Assignment[];
  people: Person[];
  onChanged: () => void;
  onError: (s: string | null) => void;
  onOpenPerson: (employeeId: string) => void;
}) {
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);

  const peopleBy = useMemo(() => new Map(people.map(p => [p.employee_id, p])), [people]);

  const groups = useMemo(() => {
    const m = new Map<string, Requirement[]>();
    for (const r of requirements) {
      const k = r.alt_group ?? `solo:${r.requirement_id}`;
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return Array.from(m.entries());
  }, [requirements]);

  // PromiseLike, not Promise: a Supabase query builder is a thenable - it has
  // .then() but no .catch or .finally - so typing this as Promise rejects every
  // caller.
  async function run(fn: () => PromiseLike<{ error: any }>, what: string) {
    setBusy(true); onError(null);
    const { error } = await fn();
    setBusy(false);
    if (error) { onError(`Couldn't ${what}: ${error.message}`); return; }
    onChanged();
  }

  // Ticket types this role does not already require, filtered by whatever has
  // been typed. Name, code and category all match, because James thinks in all
  // three depending on what he is looking at.
  const unused = licenceTypes.filter(
    l => !requirements.some(r => r.licence_type_id === l.licence_type_id));
  const needle = adding.trim().toLowerCase();
  const pickList = (needle
    ? unused.filter(l =>
        l.display_name.toLowerCase().includes(needle)
        || l.code.toLowerCase().includes(needle)
        || (l.category ?? "").toLowerCase().includes(needle))
    : unused
  ).slice(0, 60);

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-[16px] font-semibold text-text">{role.display_name}</h2>
              <div className="text-[11px] text-text-muted mt-0.5 font-mono">{role.code}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Pill label={ROLE_KIND_META[role.kind]?.label ?? role.kind} tone="default" small />
              {role.is_baseline && <Pill label="held by everyone" tone="ok" small />}
              {role.grade_family && <Pill label="has classes" tone="notice" small />}
              {role.auto_grant && <Pill label="auto" tone="notice" small />}
            </div>
          </div>
          {role.notes && <p className="text-[12px] text-text-muted mt-2 italic">{role.notes}</p>}
          {role.auto_grant && (
            <p className="text-[11.5px] text-text-muted mt-2 flex items-start gap-1.5">
              <Sparkles size={12} className="mt-0.5 flex-shrink-0 text-accent" />
              Loading any of the tickets below onto someone's record gives them this role
              automatically. Nobody has to remember to tick it.
            </p>
          )}
        </div>
      </Card>

      {/* ---- the class ladder, if this role has one ------------------------ */}
      {role.grade_family && (
        <Card>
          <div className="p-5">
            <h3 className="text-[13px] font-semibold text-text mb-1">Classes</h3>
            <p className="text-[11px] text-text-muted mb-3">
              Set on the person, not here. Whichever class they hold, the requirement is a
              ticket at that class <em>or higher</em> — so an Advanced ticket satisfies a Basic
              requirement without anyone having to say so.
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {grades.map((g, i) => (
                <span key={g.grade_code} className="inline-flex items-center gap-1.5">
                  {i > 0 && <ChevronRight size={12} className="text-text-dim" />}
                  <span className="px-2 py-1 rounded border border-line text-[11.5px] text-text">
                    {g.grade_label}
                    <span className="text-[10px] text-text-dim font-mono ml-1.5">
                      {licenceBy.get(g.licence_type_id)?.code ?? "—"}
                    </span>
                  </span>
                </span>
              ))}
              {grades.length === 0 && (
                <div className="text-[12px] text-warn flex items-start gap-1.5">
                  <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-medium">This role's classes are empty, so the class dropdown on a person has nothing in it.</span>
                    <span className="block text-text-muted mt-0.5">
                      A class ladder is one ticket type per class, in order — for rigging that's
                      Basic, Intermediate, Advanced. It's empty because no ticket type in the
                      licence list matched the codes the migration looked for. Tell Claude which
                      ticket is which class and it's a one-line fix.
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ---- requirements ------------------------------------------------- */}
      <Card>
        <div className="p-5">
          <h3 className="text-[13px] font-semibold text-text mb-1">
            {role.grade_family ? "What it requires on top of the class" : "What this role requires"}
          </h3>
          <p className="text-[11px] text-text-muted mb-3">
            Give two or more the same <span className="text-text">alternative group</span> and holding
            any one of them satisfies it.
          </p>

          {groups.length === 0 ? (
            <div className="text-[12px] text-text-muted py-3">
              Nothing extra required. {role.grade_family
                ? "The class ticket above is the whole requirement."
                : "Anyone holding this role is measured only on the baseline."}
            </div>
          ) : (
            <div className="divide-y divide-line border border-line rounded-md overflow-hidden mb-3">
              {groups.map(([key, list]) => (
                <div key={key} className="px-3 py-2.5">
                  {list.length > 1 && (
                    <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">
                      Any one of these
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {list.map(r => {
                      const lt = licenceBy.get(r.licence_type_id);
                      return (
                        <div key={r.requirement_id} className="flex items-center gap-3 flex-wrap">
                          <span className="text-[13px] text-text flex-1 min-w-[160px]">
                            {lt?.display_name ?? "Unknown ticket"}
                            <span className="text-[10px] text-text-dim font-mono ml-1.5">{lt?.code}</span>
                          </span>

                          <label className="inline-flex items-center gap-1.5 text-[11px] text-text-muted"
                                 title="Off means it shows on the person's record but doesn't count against their percentage.">
                            <input type="checkbox" checked={r.is_mandatory} disabled={busy}
                              onChange={e => run(() => supabase.from("role_requirements")
                                .update({ is_mandatory: e.target.checked })
                                .eq("requirement_id", r.requirement_id), "change that")} />
                            required
                          </label>

                          {/* Uncontrolled, saved on blur, so typing does not write per
                              keystroke. The key resets it after a refetch. */}
                          <input
                            key={`${r.requirement_id}:${r.alt_group ?? ""}`}
                            defaultValue={r.alt_group ?? ""}
                            disabled={busy}
                            placeholder="alt group"
                            title="Requirements sharing this label are alternatives — holding any one satisfies it."
                            onBlur={e => {
                              const v = e.target.value.trim() || null;
                              if (v !== r.alt_group) {
                                run(() => supabase.from("role_requirements")
                                  .update({ alt_group: v }).eq("requirement_id", r.requirement_id),
                                  "change that");
                              }
                            }}
                            className="w-24 bg-ink border border-line rounded px-2 py-1 text-[11px] text-text"
                          />

                          <button type="button" disabled={busy}
                            onClick={() => run(() => supabase.from("role_requirements")
                              .delete().eq("requirement_id", r.requirement_id), "remove that requirement")}
                            className="text-text-muted hover:text-danger" title="Remove this requirement">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ---- the picker ---------------------------------------------------
              Was a <select> with optgroups. James: "that list is all over the
              place and missing key things... maybe it's the electrical throwing
              me off." A native select can't be searched, can't show a code, and
              buries eighty entries behind a scroll, so the fix is not to reorder
              it - it is to let you type.

              It also now tells you plainly when a ticket type does not exist at
              all, which is the actual reason things looked "missing".        */}
          <div className="border border-line rounded-md overflow-hidden">
            <div className="p-2 border-b border-line">
              <Input value={adding} onChange={e => setAdding(e.target.value)}
                     placeholder="Add a ticket — type a name or a code…" />
            </div>
            <div className="max-h-56 overflow-auto divide-y divide-line">
              {pickList.length === 0 ? (
                <div className="px-3 py-3 text-[12px] text-text-muted">
                  {adding.trim()
                    ? <>Nothing in the licence list matches “{adding.trim()}”. If the ticket type
                        doesn't exist yet it has to be created in the licence list first — it can't
                        be required until it's something a person can hold.</>
                    : <>Every ticket type is already required by this role.</>}
                </div>
              ) : pickList.map(l => (
                <button key={l.licence_type_id} type="button" disabled={busy}
                  onClick={() => {
                    run(() => supabase.from("role_requirements").insert({
                      role_id: role.role_id, licence_type_id: l.licence_type_id, is_mandatory: true,
                    }), "add that requirement");
                    setAdding("");
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-ink-hover flex items-center gap-3">
                  <Plus size={12} className="text-accent flex-shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="text-[12.5px] text-text block truncate">{l.display_name}</span>
                    <span className="text-[10px] text-text-dim">{l.category}</span>
                  </span>
                  <span className="text-[10px] font-mono text-text-muted flex-shrink-0">{l.code}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* ---- who holds it, read-only --------------------------------------- */}
      <Card>
        <div className="p-5">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <h3 className="text-[13px] font-semibold text-text">Who holds this</h3>
            <span className="text-[11px] text-text-muted">{holders.length} assigned</span>
          </div>
          {role.is_baseline ? (
            <p className="text-[12px] text-text-muted">
              Every field employee holds this automatically. It can't be taken off anyone.
            </p>
          ) : holders.length === 0 ? (
            <p className="text-[12px] text-text-muted">
              Nobody yet. Assign it on the People tab, on the person.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {holders.map(h => {
                const p = peopleBy.get(h.employee_id);
                const g = grades.find(x => x.grade_code === h.grade);
                return (
                  <button key={h.employee_role_id} type="button"
                    onClick={() => onOpenPerson(h.employee_id)}
                    className="px-2 py-1 rounded border border-line text-[11.5px] text-text hover:border-accent hover:text-accent transition-colors">
                    {p ? `${p.first_name} ${p.last_name}` : "Unknown"}
                    {g && <span className="text-text-muted"> · {g.grade_label}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
